import { PayPalCheckout } from "braintree-web";
import { PAYMENT_GATEWAY } from "../../../core/constants/constants";
import * as $numberHelper from '../../../core/helpers/number.helper';
import { PaymentGatewayModel } from "../../../core/models/payment-gateway.model";
import { SurchargeModel } from "../../../core/models/surcharge.model";
import { UiStringModel } from "../../../core/models/ui-string.model";
import { CheckoutForm } from "../booking-form.const";

export class CheckoutPayPal {
  private readonly hasLoaded = ko.observable(false);
  private readonly surchargeLoaded = ko.observable(false);

  public readonly enabled = ko.observable(false)
  public readonly ready: KnockoutComputed<boolean>;

  public readonly surcharge = ko.observable(0);

  public readonly orderId = ko.observable('');
  public readonly nonce = ko.observable('');
  public readonly payerEmail = ko.observable('');
  public readonly payerFirstName = ko.observable('');
  public readonly payerLastName = ko.observable('');
  public readonly payerAddressLine1 = ko.observable<string | undefined>('');
  public readonly payerAddressLine2 = ko.observable<string | undefined>('');
  public readonly payerAddressCountryCode = ko.observable<string | undefined>('');
  public readonly payerAddressPostalCode = ko.observable<string | undefined>('');
  public readonly payerAddressState = ko.observable<string | undefined>('');
  public readonly payerPhoneNumber = ko.observable<string | undefined>('');

  constructor(
    private readonly paymentGatewayId: KnockoutObservable<number>,
    private readonly shoppingCartTotalAmount: KnockoutObservable<number>,
    private readonly isLoading: KnockoutObservable<boolean>,
    private readonly disableAlternativePayments: KnockoutObservable<boolean>,
    private readonly setErrorMessage: (error: string) => void,
    private readonly onCancel: () => void,
    private readonly uiStrings: UiStringModel,
    private readonly form: CheckoutForm,
    ) {

    this.ready = ko.pureComputed(() => this.hasLoaded() && this.surchargeLoaded());
  }

  public applySurcharge(surcharges: SurchargeModel[]) {
    const surcharge = surcharges.find(x => x.paymentTypeName === 'PayPal');
    if (surcharge) {
        this.surcharge(surcharge.percentage);
    }
    this.surchargeLoaded(true);
  }

  public async initialise(paypalGateway: PaymentGatewayModel) {
    const braintreeToken = paypalGateway.paymentGatewayToken?.split('/')[paypalGateway.paymentGatewayToken.split('/').length - 1];

    if (!braintreeToken) {
      this.enabled(false);
      return;
    }

    this.enabled(true);

    await $.when(
      $.getScript({
        url: 'https://js.braintreegateway.com/web/3.91.0/js/paypal-checkout.min.js',
        dataType: "script",
        cache: true,
      }),
      $.getScript({
        url: 'https://js.braintreegateway.com/web/3.91.0/js/client.min.js',
        dataType: "script",
        cache: true,
      })
    );

    await this.initialiseBraintree(braintreeToken, paypalGateway);
  }

  private async initialiseBraintree(braintreeToken: string, paypalGateway: PaymentGatewayModel) {
    const client = await window.braintree.client.create({ authorization: braintreeToken })

    const checkout = await window.braintree.paypalCheckout.create({ client });

    await checkout.loadPayPalSDK({
      intent: 'authorize',
      currency: this.uiStrings.currencyCode,
      "disable-funding": 'card',
      commit: false,
    });

    const button = window.paypal.Buttons({  
      style: {
        layout: 'horizontal',
        color: 'white',
        tagline: false,
        height: 40,
      } as paypal.ButtonStyle,
      onInit: (data: paypal.AuthorizationData, actions: any) => this.onInit(data, actions),
      onClick: () => this.onClick(paypalGateway),
      createOrder: () => this.createOrder(checkout),
      onApprove: (data: paypal.AuthorizationData, actions: object) => this.onApprove(checkout, data, actions),
      onCancel: (_: paypal.CancellationData, __: object) => this.onCancel(),
      onError: (err: string) => this.onPayPalError(err)
    });
      
    button.render('#paypal-button');
  }

  private onInit(_: paypal.AuthorizationData, actions: { disable: () => void; enable: () => void;}) {
    this.hasLoaded(true);

    if (!this.form.bookingEmail.isValid()) {
      actions.disable();
    }

    this.form.bookingEmail.subscribe(() => {
      if (this.form.bookingEmail.isValid()) {
        actions.enable();
      } else {
        actions.disable();
      }
    });
  }

  private onClick(paypalGateway: PaymentGatewayModel) {
    this.paymentGatewayId(paypalGateway.paymentGatewayId);

    this.form.triggerEmailValidation();
  }

  private createOrder(checkout: PayPalCheckout): Promise<string> {
    const totalSurcharge = this.shoppingCartTotalAmount() * (this.surcharge() / 100);
    const value = $numberHelper.Round(this.shoppingCartTotalAmount(), 2) + $numberHelper.Round(totalSurcharge, 2);
    return checkout.createPayment({
        flow: 'checkout' as paypal.FlowType.Checkout,
        amount: $numberHelper.ToPayPal(value),
        currency: this.uiStrings.currencyCode,
        enableShippingAddress: false,
        landingPageType: 'login'
    });
  }

  private async onApprove(checkout: PayPalCheckout, data: paypal.AuthorizationData, actions: object): Promise<paypal.AuthorizationResponse> {
    this.isLoading(true);

    const response = await checkout.tokenizePayment(data);
    
    this.orderId((data as any).orderID);
    this.nonce(response.nonce);
    this.payerEmail(response.details.email);
    this.payerFirstName(response.details.firstName);
    this.payerLastName(response.details.lastName);
    this.payerAddressLine1(response.details.billingAddress?.line1);
    this.payerAddressLine2(response.details.billingAddress?.line2);
    this.payerAddressCountryCode(response.details.billingAddress?.countryCode);
    this.payerAddressPostalCode(response.details.billingAddress?.postalCode);
    this.payerAddressState(response.details.billingAddress?.state);
    this.payerPhoneNumber(response.details.phone?.replace(/[-()+ ]/g, ''));

    $('#paypal-form').trigger('submit');

    return response;
  }

  private onPayPalError(error: string) {
    console.error(error);
    this.setErrorMessage('Error processing PayPal transaction, please try again.');
  }
}